import React, { useRef, useEffect } from 'react';
import { markersToHtml, htmlToMarkers } from '../../utils/richTextEditor';

/**
 * Real rich-text editing for admin-authored long-form content (course
 * transcripts, sequence emails, Hub announcements) — replaces a plain
 * <textarea>. A textarea has no formatting engine, so Cmd/Ctrl+B does
 * nothing in one; that's not a bug, it's just what a textarea is.
 *
 * This is a contenteditable region instead, which responds to real OS/
 * browser keyboard shortcuts natively — Cmd/Ctrl+B, Cmd/Ctrl+I, and the
 * native text-selection toolbar on mobile — with no JS required for the
 * shortcut itself. The toolbar buttons below are a secondary, discoverable
 * path to the same actions (execCommand), not the primary interaction.
 *
 * Storage format is unchanged: plain text with "**bold**", "_italic_", and
 * "- " bullet markers, the same convention renderRichText already displays, every
 * existing reader of this content (Lesson.jsx, AnnouncementDetail.jsx,
 * the admin previews) already expects. This component only changes how
 * that text gets typed, not how it's stored or rendered.
 */
export default function RichTextEditor({ value, onChange, minHeightClass = 'min-h-[200px]', placeholder }) {
  const ref = useRef(null);
  // A sentinel, not `value` itself — initializing this to `value` meant the
  // very first render always saw value === lastEmitted.current and skipped
  // the sync, leaving a freshly-mounted editor's DOM empty even though its
  // value prop (and the surrounding state) held real content. Editing an
  // existing item, or a row created by pasting a spreadsheet range, both
  // mount a fresh RichTextEditor with non-empty initial content — this bug
  // made both look blank, and blurring the field then wrote that emptiness
  // back into state, genuinely losing the content on save.
  const lastEmitted = useRef(Symbol('unset'));

  // Only push value -> DOM when it changed from *outside* this editor
  // (e.g. switching which item is being edited, or the initial mount) —
  // never on our own typing, or the cursor would jump to the start on
  // every keystroke.
  useEffect(() => {
    if (value === lastEmitted.current) return;
    if (ref.current) ref.current.innerHTML = markersToHtml(value);
    lastEmitted.current = value;
  }, [value]);

  const emitChange = () => {
    if (!ref.current) return;
    const next = htmlToMarkers(ref.current);
    lastEmitted.current = next;
    onChange(next);
  };

  // Pasted plain text normally arrives as one blob with literal "\n"
  // characters, which CSS collapses to a single space in a normal
  // (non `white-space: pre`) contenteditable — visually swallowing every
  // return the person typed in their source doc. The only way a return
  // survived was pressing Shift+Return (a real <br>), which isn't how
  // anyone actually writes. Jenn, 2026-09-01: "when I paste the text in,
  // it doesn't recognize returns as new lines... it wants shift-return."
  // This also happened to be why the List button could only ever bulletundefined
  // the *entire* pasted blob — toggleBulletList operates on root's
  // top-level children, and an unsplit paste was exactly one such child.
  // Splitting every line into its own top-level block on paste fixes both
  // at once.
  const handlePaste = (e) => {
    e.preventDefault();
    const text = (e.clipboardData || window.clipboardData).getData('text/plain');
    if (!text) return;
    const escape = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const html = text
      .split(/\r\n|\r|\n/)
      .filter(line => line.trim())
      .map(line => `<div>${escape(line)}</div>`)
      .join('');
    document.execCommand('insertHTML', false, html);
    emitChange();
  };

  const runCommand = (command) => {
    document.execCommand(command, false, null);
    ref.current?.focus();
    emitChange();
  };

  // document.execCommand('insertUnorderedList') only reliably converts a
  // single block — across a multi-paragraph selection (the normal case:
  // highlight several lines, click "List") Chrome/Safari often bullet just
  // the first block and leave the rest untouched. Jenn hit this directly:
  // had to select and convert one line at a time. Worse, each one-at-a-time
  // conversion produces its own separate single-item <ul>, and a saved
  // single-item list block never round-trips as a real list (markersToHtml
  // and renderRichText both require 2+ "- " lines in one block to treat it
  // as a bullet list) — so the bullets were silently reverting to plain
  // "- "-prefixed text, unindented, both back in this editor and on the
  // real member-facing page. This walks the selection's own top-level
  // blocks and merges all of them into one shared <ul>, which is what both
  // of those readers actually expect.
  // `node`/`offset` come straight from a Range boundary. Usually node is
  // somewhere inside one of root's blocks and we just walk up to find which
  // one. But Cmd/Ctrl+A (a completely normal way to select everything before
  // bulleting the whole box) produces a Range whose container IS root
  // itself — offset 0 at the start, root.childNodes.length at the end —
  // so there's no ancestor walk to do; the offset has to be resolved to
  // one of root's actual children directly.
  const findTopLevelChild = (root, node, offset) => {
    if (node === root) {
      if (!root.childNodes.length) return null;
      return root.childNodes[Math.min(offset, root.childNodes.length - 1)];
    }
    while (node && node.parentNode !== root) node = node.parentNode;
    return node;
  };

  const toggleBulletList = () => {
    const root = ref.current;
    if (!root) return;
    root.focus();
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);

    const startNode = findTopLevelChild(root, range.startContainer, range.startOffset);
    const endNode = findTopLevelChild(root, range.endContainer, range.endOffset);
    if (!startNode || !endNode) return;

    const children = Array.from(root.childNodes);
    const startIdx = children.indexOf(startNode);
    const endIdx = children.indexOf(endNode);
    if (startIdx === -1 || endIdx === -1) return;
    const [lo, hi] = startIdx <= endIdx ? [startIdx, endIdx] : [endIdx, startIdx];
    const targets = children.slice(lo, hi + 1);
    if (!targets.length) return;

    // Selection lies entirely inside one existing list — toggle it back off.
    if (targets.length === 1 && targets[0].nodeType === Node.ELEMENT_NODE &&
        ['UL', 'OL'].includes(targets[0].tagName)) {
      const list = targets[0];
      Array.from(list.children).forEach(li => {
        const div = document.createElement('div');
        div.innerHTML = li.innerHTML;
        list.parentNode.insertBefore(div, list);
      });
      list.remove();
      emitChange();
      return;
    }

    // Otherwise merge every targeted block into one shared bullet list —
    // absorbing any list already among them so a mixed selection still
    // ends up as a single <ul>, not several.
    const ul = document.createElement('ul');
    targets.forEach(node => {
      if (node.nodeType === Node.TEXT_NODE && !node.textContent.trim()) return; // stray whitespace between blocks — not real content
      if (node.nodeType === Node.ELEMENT_NODE && ['UL', 'OL'].includes(node.tagName)) {
        Array.from(node.children).forEach(li => ul.appendChild(li));
      } else {
        const li = document.createElement('li');
        if (node.nodeType === Node.TEXT_NODE) li.textContent = node.textContent;
        else li.innerHTML = node.innerHTML;
        ul.appendChild(li);
      }
    });
    if (!ul.children.length) return; // selection was only whitespace between blocks — nothing to bullet
    targets[0].parentNode.insertBefore(ul, targets[0]);
    targets.forEach(node => node.remove());

    emitChange();
  };

  // Soft grey text, stored as ^^marker^^ (renderRichText + emails show it grey).
  // Clicking inside existing grey text turns it back to normal. A selection
  // across several paragraphs greys each one, so no block ends up inside a span.
  const toggleGrey = () => {
    const root = ref.current;
    const sel = window.getSelection();
    if (!root || !sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    if (!root.contains(range.commonAncestorContainer)) return;
    const anchor = range.commonAncestorContainer.nodeType === Node.ELEMENT_NODE ? range.commonAncestorContainer : range.commonAncestorContainer.parentElement;
    const existing = anchor && anchor.closest('[data-grey]');
    if (existing && root.contains(existing)) {
      while (existing.firstChild) existing.parentNode.insertBefore(existing.firstChild, existing);
      existing.remove();
      emitChange();
      return;
    }
    if (range.collapsed) return;
    const makeSpan = () => {
      const span = document.createElement('span');
      span.setAttribute('data-grey', '1');
      span.style.color = '#6B7280';
      return span;
    };
    const startNode = findTopLevelChild(root, range.startContainer, range.startOffset);
    const endNode = findTopLevelChild(root, range.endContainer, range.endOffset);
    if (startNode && endNode && startNode !== endNode) {
      const children = Array.from(root.childNodes);
      const [a, b] = [children.indexOf(startNode), children.indexOf(endNode)].sort((x, y) => x - y);
      children.slice(a, b + 1).forEach(node => {
        if (node.nodeType !== Node.ELEMENT_NODE) return;
        const holders = ['UL', 'OL'].includes(node.tagName) ? Array.from(node.children) : [node];
        holders.forEach(h => {
          if (!h.textContent.trim()) return;
          const span = makeSpan();
          while (h.firstChild) span.appendChild(h.firstChild);
          h.appendChild(span);
        });
      });
    } else {
      const span = makeSpan();
      span.appendChild(range.extractContents());
      range.insertNode(span);
    }
    root.focus();
    emitChange();
  };

  const btn = 'px-2.5 py-1 text-xs font-medium border border-gray-200 rounded-md hover:border-reset-terracotta hover:text-reset-terracotta transition-colors';

  return (
    <div>
      <div className="flex gap-1.5 mb-1.5">
        <button type="button" onClick={() => runCommand('bold')} className={`${btn} font-bold`} title="Bold (Cmd/Ctrl+B)">B</button>
        <button type="button" onClick={() => runCommand('italic')} className={`${btn} italic`} title="Italic (Cmd/Ctrl+I)">i</button>
        <button type="button" onClick={toggleBulletList} className={btn} title="Bullet list — select one or more lines first">• List</button>
        <button type="button" onClick={toggleGrey} className={`${btn} text-gray-500`} title="Soft grey text — select text first; click inside grey text to turn it back">Grey</button>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={emitChange}
        onBlur={emitChange}
        onPaste={handlePaste}
        data-placeholder={placeholder}
        className={`w-full border border-gray-200 rounded-lg px-3 py-2 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-reset-terracotta overflow-y-auto [&>*+*]:mt-3 [&>ul]:list-disc [&>ul]:pl-5 [&>ul]:space-y-1 ${minHeightClass} empty:before:content-[attr(data-placeholder)] empty:before:text-gray-400`}
      />
      <p className="text-[10px] text-gray-400 mt-1">
        Select text and use Cmd/Ctrl+B or Cmd/Ctrl+I, or the buttons above (Grey makes a soft grey note). Works the same on Mac, PC, and mobile.
      </p>
    </div>
  );
}
